import { useRef, useState, useEffect } from 'react';
import { EFFECT_VALUE_MAX, EFFECT_VALUE_MIN } from 'constants/params';
import { InputBase } from 'components/UI/input/elements/input-base';
import { ConfigFlags as FileConfigFlags } from 'services/file.service';

import styles from '../_editor-config.module.scss';
import { InputRange } from 'components/UI/input/elements/input-range';

type SliderParams = {
  min: number;
  max: number;
  default: number;
  step: number;
};

interface Props {
  params: SliderParams;
  value: number;
  setValue: (value: number) => void;
  onSubmit: () => void;
  configKey?: FileConfigFlags;
  onHoverChange?: (isHovered: boolean) => void;
}

export const ConfigSlider = ({ params, value, setValue, onSubmit, configKey, onHoverChange }: Props) => {
  const [inputFocused, setInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const prevVal = useRef(value);

  const isHighlightSensitivity = configKey === FileConfigFlags.highlightSensitivity;

  // Track when Highlight Sensitivity slider is active and notify parent
  useEffect(() => {
    if (!isHighlightSensitivity || !onHoverChange) return;
    const isActive = inputFocused || isDragging;
    onHoverChange(isActive);
  }, [inputFocused, isDragging, isHighlightSensitivity, onHoverChange]);

  const onPrevValReset = () => (prevVal.current = value);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const regEx = /^-?\d{0,3}([.,]\d?)?$/;
    if (!regEx.test(e.target.value)) return;
    const val = e.target.value;
    const int = parseInt(val, 10);
    if (int < EFFECT_VALUE_MIN || int > EFFECT_VALUE_MAX) return;
    setInputValue(val);
    const res = +val.replace(/,/, '.');
    if (!Number.isNaN(res)) setValue(res);
  };

  const onDoubleClick = () => {
    if (prevVal.current === params.default) return;
    prevVal.current = params.default;
    setInputValue(`${params.default}`);
    setValue(params.default);
    onSubmit();
  };

  const handleSubmit = () => {
    if (prevVal.current === value) return;
    onSubmit();
  };

  const onBlur = () => {
    setInputFocused(false);
    handleSubmit();
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    setInputValue(`${value}`);
    setInputFocused(true);
    e.target.select();
  };

  const handleMouseDown = () => {
    if (isHighlightSensitivity) {
      setIsDragging(true);
    }
  };

  const handleMouseUp = () => {
    if (isHighlightSensitivity) {
      setIsDragging(false);
    }
    handleSubmit();
  };

  const handleMouseEnter = () => {
    if (isHighlightSensitivity && onHoverChange) {
      onHoverChange(true);
    }
  };

  const handleMouseLeave = () => {
    if (isHighlightSensitivity && !inputFocused && !isDragging && onHoverChange) {
      onHoverChange(false);
    }
  };

  return (
    <div
      className={styles.Slider}
      onMouseDown={onPrevValReset}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.Slider__track}>
        <InputRange
          value={value}
          onChange={(e) => setValue(parseFloat(e.target.value))}
          onKeyUp={(e) =>
            (e.code === 'ArrowUp' || e.code === 'ArrowDown' || e.code === 'ArrowLeft' || e.code === 'ArrowRight') &&
            onSubmit()
          }
          onDoubleClick={onDoubleClick}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          {...params}
        />
      </div>
      <InputBase
        className={styles.Slider__val}
        value={inputFocused ? inputValue : value.toFixed(1)}
        onChange={onInputChange}
        onDoubleClick={onDoubleClick}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    </div>
  );
};
